import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { MapPin, ArrowRight, Search } from "lucide-react";
import PublicLayout from "../components/PublicLayout";
import SEO from "../components/SEO";
import { apiClient } from "../lib/api";
import CommuneIndex from "../components/CommuneIndex";
import { DEPARTMENTS } from "../components/Footer";

const PAGE = 24;

export default function CitiesPage() {
  const [cities, setCities] = useState([]);
  const [sortBy, setSortBy] = useState("score");
  const [q, setQ] = useState("");
  const [dept, setDept] = useState("all");
  const [limit, setLimit] = useState(PAGE);
  const depts = useMemo(() => [...new Set(cities.map((c) => c.department))], [cities]);
  const norm = (s) => s.normalize("NFD").replace(/[\u0300-\u036f]/g, "").toLowerCase();

  useEffect(() => {
    apiClient.get("/cities").then((r) => setCities(r.data));
  }, []);

  const sorted = cities.filter((c) => (dept === "all" || c.department === dept) && norm(c.name).includes(norm(q))).sort((a, b) => {
    if (sortBy === "score") return b.investment_score - a.investment_score;
    if (sortBy === "occupancy") return b.occupancy_rate - a.occupancy_rate;
    if (sortBy === "adr") return b.adr - a.adr;
    if (sortBy === "name") return a.name.localeCompare(b.name, "fr");
    return 0;
  });

  return (
    <PublicLayout>
      <SEO
        title={`Conciergerie Airbnb dans ${cities.length || 1308} communes de Bretagne : comparez`}
        description="Comparez la rentabilité Airbnb de 1308 communes de toute la Bretagne : Dinard, Cancale, Dinan, Rennes, Saint-Brieuc, Erquy, Granville, Mont-Saint-Michel… Revenus, occupation, devis conciergerie."
      />
      <section className="pt-12 pb-8 md:pt-20 md:pb-12">
        <div className="container-tm">
          <span className="pill">Comparateur</span>
          <h1 className="h-display mt-5 max-w-4xl">
            Rentabilité Airbnb<br />
            <span className="text-accent">ville par ville</span>
          </h1>
          <p className="mt-6 text-muted-foreground max-w-2xl text-lg">
            {cities.length || 1308} communes analysées partout en Bretagne : revenus annuels, occupation, saisonnalité,
            événements rentables, et devis conciergerie gratuit pour chaque commune.
          </p>
        </div>
      </section>

      <section className="pb-20">
        <div className="container-tm">
          <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 mb-10">
            <div className="flex flex-col gap-3 md:max-w-sm w-full">
              <div className="relative">
                <Search size={16} className="absolute left-4 top-1/2 -translate-y-1/2 text-muted-foreground" />
                <input value={q} onChange={(e) => { setQ(e.target.value); setLimit(PAGE); }} placeholder="Rechercher votre commune…"
                  className="w-full pl-11 pr-5 py-3 border border-input rounded-full bg-white focus:outline-none focus:ring-2 focus:ring-accent/30" data-testid="cities-search" />
              </div>
              <div className="text-sm text-muted-foreground" data-testid="cities-count">{sorted.length} communes · Triées par</div>
            </div>
            <div className="flex flex-wrap gap-2 bg-white border border-border rounded-full p-1.5" data-testid="sort-controls">
              {[
                { k: "score", label: "Note investisseur" },
                { k: "occupancy", label: "Occupation" },
                { k: "adr", label: "Prix / nuit" },
                { k: "name", label: "A → Z" },
              ].map((o) => (
                <button
                  key={o.k}
                  onClick={() => setSortBy(o.k)}
                  data-testid={`sort-${o.k}`}
                  className={`text-sm font-medium px-4 py-2 rounded-full transition-colors ${
                    sortBy === o.k
                      ? "bg-primary text-primary-foreground"
                      : "text-foreground/70 hover:bg-secondary"
                  }`}
                >
                  {o.label}
                </button>
              ))}
            </div>
          </div>

          <div className="flex flex-wrap items-center gap-x-4 gap-y-2 mb-6 text-sm" data-testid="dept-page-links">
            <span className="font-semibold">Conciergerie par département :</span>
            {DEPARTMENTS.map(([s, n]) => (
              <Link key={s} to={`/departements/${s}`} className="text-accent hover:underline" data-testid={`dept-page-link-${s}`}>{n}</Link>
            ))}
          </div>
          <div className="flex flex-wrap gap-2 mb-8" data-testid="dept-filters">
            {["all", ...depts].map((d) => (
              <button key={d} onClick={() => { setDept(d); setLimit(PAGE); }} data-testid={`dept-${d}`}
                className={`text-xs font-semibold px-3 py-1.5 rounded-full border ${dept === d ? "bg-accent text-accent-foreground border-accent" : "border-border bg-white"}`}>
                {d === "all" ? "Tous les départements" : d}
              </button>
            ))}
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5 md:gap-6">
            {sorted.slice(0, limit).map((c, idx) => (
              <motion.div
                key={c.slug}
                initial={{ opacity: 0, y: 16 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: (idx % 6) * 0.04 }}
              >
                <Link to={`/villes/${c.slug}`} className="block group" data-testid={`city-link-${c.slug}`}>
                  <div className="relative aspect-[4/5] overflow-hidden rounded-3xl bg-secondary">
                    <img src={c.image} alt={`Airbnb et conciergerie à ${c.name}`} loading="lazy" className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-700" />
                    <div className="absolute inset-0 bg-gradient-to-t from-primary/85 via-primary/20 to-transparent" />
                    <div className="absolute top-4 left-4 px-3 py-1.5 bg-white rounded-full text-xs font-bold">
                      {c.investment_score.toFixed(1)}
                    </div>
                    <div className="absolute top-4 right-4 px-3 py-1.5 bg-accent text-accent-foreground rounded-full text-xs font-bold">
                      {c.occupancy_rate}% occup.
                    </div>
                    <div className="absolute bottom-0 inset-x-0 p-6 text-white">
                      <div className="text-xs uppercase tracking-widest text-white/70 flex items-center gap-1">
                        <MapPin size={11} /> {c.department}
                      </div>
                      <h3 className="text-3xl font-bold mt-1.5" style={{ fontFamily: "Quicksand, sans-serif", letterSpacing: "-0.01em" }}>{c.name}</h3>
                      <p className="text-white/80 text-sm mt-2 line-clamp-2">{c.tagline}</p>
                      <div className="mt-5 pt-5 border-t border-white/20 flex items-center justify-between">
                        <div>
                          <div className="text-[10px] uppercase tracking-widest text-white/60">€/nuit</div>
                          <div className="text-[hsl(var(--ocean))] font-bold text-lg">{c.adr} €</div>
                        </div>
                        <div className="opacity-0 group-hover:opacity-100 transition-opacity">
                          <ArrowRight size={20} className="text-white" />
                        </div>
                      </div>
                    </div>
                  </div>
                </Link>
              </motion.div>
            ))}
          </div>
          {limit < sorted.length && (
            <div className="text-center mt-10">
              <button onClick={() => setLimit((l) => l + PAGE)} className="btn-ghost" data-testid="cities-more">
                Voir plus de communes ({sorted.length - limit} restantes)
              </button>
            </div>
          )}
        </div>
      </section>

      <CommuneIndex cities={cities} />
    </PublicLayout>
  );
}
